/**
 * ISO 639-1 language codes → ISO 3166-1 alpha-2 region codes for lipis/flag-icons (`fi fi-xx`).
 * One representative region per language (language ≠ country; this picks a conventional flag).
 * Three-letter keys cover common ISO 639-2 / API variants (e.g. fil, zho).
 */

const ISO639_1_PAIRS = `
aa et ab ge ae ir af za ak gh am et an es ar sa as in av ru ay bo az az ba ru be by bg bg bi vu bm ml bn bd bo cn br fr bs ba ca es ce ru ch gu co fr cr ca cs cz cu ru cv ru cy gb da dk de de dv mv dz bt ee gh el gr en us eo un es es et ee eu es fa ir ff sn fi fi fj fj fo fo fr fr fy nl ga ie gd gb gl es gn py gu in gv gb ha ng he il hi in ho pg hr hr ht ht hu hu hy am hz na ia un id id ie un ig ng ii cn ik us io un is is it it iu ca ja jp jv id ka ge kg cd ki ke kj na kk kz kl gl km kh kn in ko kr kr ng ks in ku iq kv ru kw gb ky kg la va lb lu li nl ln cd lo la lt lt lu cd lv lv mg mg mh mh mi nz mk mk ml in mn mn mr in ms my mt mt my mm na nr nb no nd zw ne np ng na nl nl nn no nr za nv us ny mw oc fr oj ca om et or in os ru pa in pi in pl pl ps af pt br qu pe rm ch rn bi ro ro ru ru rw rw sa in sc it sd pk se no sg cf si lk sk sk sl si sm ws sn zw so so sq al sr rs ss sz st za su id sv se sw ke ta in te in tg tj th th ti er tk tm tl ph tn bw to to tr tr ts za tt ru tw gh ty pf ug cn uk ua ur pk uz uz ve za vi vn vo un wa be wo sn xh za yi il yo ng za cn zh cn zu za
`.trim().split(/\s+/);

function pairsToRecord(pairs: string[]): Record<string, string> {
  const o: Record<string, string> = {};
  for (let i = 0; i < pairs.length; i += 2) {
    const k = pairs[i]?.toLowerCase();
    const v = pairs[i + 1]?.toLowerCase();
    if (k && v) o[k] = v;
  }
  return o;
}

/** All standard ISO 639-1 two-letter codes mapped to a flag region. */
export const ISO639_1_TO_REGION = pairsToRecord(ISO639_1_PAIRS);

/** Common ISO 639-2 / BCP47-style three-letter language codes → region. */
export const ISO639_2_COMMON_TO_REGION: Record<string, string> = {
  // Chinese
  zho: 'cn',
  chi: 'cn',
  // English / Portuguese / Norwegian variants
  eng: 'us',
  por: 'br',
  spa: 'es',
  fre: 'fr',
  ger: 'de',
  dut: 'nl',
  gle: 'ie',
  may: 'my',
  // Filipino / Tagalog
  fil: 'ph',
  tgl: 'ph',
  // Serbo-Croatian legacy
  hrv: 'hr',
  srp: 'rs',
  bos: 'ba',
  // Macro languages
  ara: 'sa',
  fas: 'ir',
  pes: 'ir',
  uzb: 'uz',
  // Indic
  hin: 'in',
  ben: 'bd',
  tam: 'in',
  tel: 'in',
  mar: 'in',
  guj: 'in',
  pan: 'in',
  urd: 'pk',
  snd: 'pk',
  nep: 'np',
  sin: 'lk',
  // Other API noise
  und: 'un',
  mul: 'un',
  zxx: 'un',
};

/** Merge: explicit ISO 639-1 + extras + three-letter. */
export const LANGUAGE_TO_FLAG_REGION: Record<string, string> = {
  ...ISO639_1_TO_REGION,
  ...ISO639_2_COMMON_TO_REGION,
  // Overrides / product choices (US English as default for `en`)
  en: 'us',
  pt: 'br',
  es: 'es',
  // Norwegian written forms → Norway
  nb: 'no',
  nn: 'no',
  no: 'no',
  // Filipino (API)
  fil: 'ph',
};

/**
 * ISO 3166-1 alpha-2 codes that exist in lipis/flag-icons (`fi fi-xx`).
 * Used to accept BCP47 region subtags (e.g. en-GB → gb, zh-TW → tw).
 */
export const ISO3166_1_ALPHA2 = new Set(
  'ad ae af ag ai al am ao aq ar as at au aw ax az ba bb bd be bf bg bh bi bj bl bm bn bo bq br bs bt bv bw by bz ca cc cd cf cg ch ci ck cl cm cn co cr cu cv cw cx cy cz de dj dk dm do dz ec ee eg eh er es et fi fj fk fm fo fr ga gb gd ge gf gg gh gi gl gm gn gp gq gr gs gt gu gw gy hk hm hn hr ht hu id ie il im in io iq ir is it je jm jo jp ke kg kh ki km kn kp kr kw ky kz la lb lc li lk lr ls lt lu lv ly ma mc md me mf mg mh mk ml mm mn mo mp mq mr ms mt mu mv mw mx my mz na nc ne nf ng ni nl no np nr nu nz om pa pe pf pg ph pk pl pm pn pr ps pt pw py qa re ro rs ru rw sa sb sc sd se sg sh si sj sk sl sm sn so sr ss st sv sx sy sz tc td tf tg th tj tk tl tm tn to tr tt tv tw tz ua ug um us uy uz va vc ve vg vi vn vu wf ws ye yt za zm zw'
    .split(/\s+/)
);

function primarySubtag(lang: string): string {
  const t = lang.trim().toLowerCase();
  if (!t) return '';
  return (t.split(/[-_]/)[0] ?? t).trim();
}

/** Rightmost 2-letter BCP47 region that is a valid ISO 3166-1 alpha-2 code (e.g. en-GB → gb, zh-Hans-CN → cn). */
function regionFromBcp47(lang: string): string | null {
  const parts = lang.replace(/_/g, '-').split('-').filter(Boolean);
  for (let i = parts.length - 1; i >= 1; i--) {
    const seg = parts[i];
    if (!/^[a-zA-Z]{2}$/.test(seg)) continue;
    const r = seg.toLowerCase();
    if (r === 'uk') return 'gb';
    if (ISO3166_1_ALPHA2.has(r)) return r;
  }
  return null;
}

/**
 * Resolves a tweet/API language tag to an ISO 3166-1 alpha-2 code for `fi fi-xx` flag-icons.
 * Order: BCP47 region (if valid) → full primary (639-1 / 639-2) → first two letters → un.
 */
export function resolveFlagCountryCode(lang: string): string {
  const raw = lang.trim();
  if (!raw) return 'un';

  const fromRegion = regionFromBcp47(raw);
  if (fromRegion) return fromRegion;

  const first = primarySubtag(raw);
  if (!first) return 'un';

  if (LANGUAGE_TO_FLAG_REGION[first]) return LANGUAGE_TO_FLAG_REGION[first];
  if (first.length > 2) {
    const three = first.slice(0, 3);
    if (LANGUAGE_TO_FLAG_REGION[three]) return LANGUAGE_TO_FLAG_REGION[three]!;
  }
  if (first.length >= 2) {
    const two = first.slice(0, 2);
    if (LANGUAGE_TO_FLAG_REGION[two]) return LANGUAGE_TO_FLAG_REGION[two];
  }
  return 'un';
}
